import * as React from "react";
import styled from "@emotion/styled";
import type { Image as ImageType } from "@shopify/hydrogen-react/dist/types/storefront-api-types.js";
import { Stack } from "atoms";
import { preventForwarding } from "styles/styleUtils";
import { useProductQuery } from "../api/useProductQuery";
import { ProductImage } from "./ProductImage";

type ProductGalleryProps = Pick<
  NonNullable<ReturnType<typeof useProductQuery>["data"]>,
  "media"
> & {
  featuredImage?: ImageType | null;
};

function ProductGallery({ featuredImage, media }: ProductGalleryProps) {
  const thumbnails = React.useMemo(
    () =>
      media?.nodes
        .map((m) => m?.previewImage)
        .filter((m): m is ImageType => !!m) ?? [],
    [media],
  );

  const [selectedId, setSelectedId] = React.useState<string | undefined>(
    !featuredImage && !!thumbnails[0]?.id ? thumbnails[0].id : undefined,
  );

  const selectedImage = React.useMemo(
    () => thumbnails.find((img) => img && img.id === selectedId),
    [thumbnails, selectedId],
  );

  React.useEffect(() => {
    if (featuredImage) {
      setSelectedId(undefined);
    }
  }, [featuredImage, setSelectedId]);

  if (!featuredImage && !thumbnails.length) {
    return null;
  }

  return (
    <Stack gap="10px">
      <FeaturedImageWrapper>
        <ProductImage
          data={selectedImage ?? featuredImage ?? thumbnails[0]}
          sizes="(min-width: 45em) 40vw, 100vw"
        />
      </FeaturedImageWrapper>

      {thumbnails.length > 1 && (
        <Thumbnails>
          {thumbnails.map((image) =>
            !image ? null : (
              <ImageWrapper key={image.id}>
                <ProductGalleryThumbnail
                  data={image}
                  sizes="(max-width: 830px) 60px, 50vw"
                  isSelected={image.id === selectedId}
                  onClick={() => setSelectedId(image.id ?? "")}
                />
              </ImageWrapper>
            ),
          )}
        </Thumbnails>
      )}
    </Stack>
  );
}

const FeaturedImageWrapper = styled.div({
  position: "relative",
});

const Thumbnails = styled.div({
  display: "flex",
  flexWrap: "wrap",
  gap: 10,
});

const ImageWrapper = styled.div({
  flex: "0 0 60px",
  aspectRatio: 1,
  height: 60,
});

type ProductGalleryThumbnailProps = { isSelected: boolean };

const ProductGalleryThumbnail = styled(ProductImage, {
  shouldForwardProp:
    preventForwarding<ProductGalleryThumbnailProps>("isSelected"),
})<ProductGalleryThumbnailProps>(({ isSelected, theme }) => ({
  aspectRatio: 1,
  width: "100%",
  height: "100%",
  objectFit: "cover",
  cursor: "pointer",
  ...(isSelected && {
    borderColor: theme.colors.fg,
  }),
}));

export { ProductGallery };
